import { useMemo } from 'react';

import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { DocumentType } from '@/types/schema';

import {
  RECOMMENDED_DOCUMENT_TYPES,
  RecommendedDocumentType,
} from '../documentUpload.constants';

export interface RecommendedDocumentStatus extends RecommendedDocumentType {
  /** True when a document of any of this entry's types has been uploaded. */
  isSatisfied: boolean;
}

/**
 * Derives, for each recommended document type, whether the user has uploaded a
 * document of that type in this session. The source of truth is the uploader's
 * `fileIDTypeMap` (fileId -> DocumentType); a type only counts once it has
 * resolved (freshly-dropped files are '' until inference/selection completes).
 */
export function useRecommendedDocumentStatus(): RecommendedDocumentStatus[] {
  const { fileIDTypeMap } = useMultiDocumentUploaderContext();

  return useMemo(() => {
    const uploadedTypes = new Set<DocumentType>(
      Object.values(fileIDTypeMap).filter(
        (type): type is DocumentType => type !== ''
      )
    );

    return RECOMMENDED_DOCUMENT_TYPES.map((recommended) => ({
      ...recommended,
      isSatisfied: recommended.documentTypes.some((type) =>
        uploadedTypes.has(type)
      ),
    }));
  }, [fileIDTypeMap]);
}
